import { cn } from '@rocco/utils/cn';
import { useFollowSelectorProxy } from '~/modules/moodboards/hooks';
import type { PinItemType } from '~/modules/moodboards/type';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { DETAIL_CTA_ICON_SIZE } from './detail-cta';

interface DetailSaveButtonProps {
    /** The pinned item id (professional / product / project ID). */
    pinId: number;
    pinType: PinItemType;
    /** `light` = dark hairline on a white section; `dark` = white on a dark section. */
    theme?: 'light' | 'dark';
    /** Extra classes (e.g. a page-specific size override). */
    className?: string;
}

/**
 * The 52px bookmark / Save pill that opens the FollowSelector (design-board)
 * flow, matching the design-system CTA height. Shared by the professional,
 * product and project detail CTAs. Relies on the global FollowSelectorProvider
 * (rendered in AppPage).
 */
export const DetailSaveButton = ({
    pinId,
    pinType,
    theme = 'light',
    className,
}: DetailSaveButtonProps) => {
    const { isInDesignBoard } = useHandleFollow();
    const { followSelector, openFollowSelector } = useFollowSelectorProxy({
        pinId,
        pinType,
    });
    const isFavourited = pinId ? isInDesignBoard(pinId, pinType) : false;
    const isDark = theme === 'dark';

    return (
        <>
            <button
                type="button"
                aria-label="Save"
                aria-pressed={isFavourited}
                onClick={(event) => {
                    event.preventDefault();
                    openFollowSelector(event);
                }}
                className={cn(
                    'inline-flex shrink-0 items-center justify-center rounded-full border transition-colors',
                    DETAIL_CTA_ICON_SIZE,
                    isDark
                        ? isFavourited
                            ? 'border-white bg-white text-[#181818]'
                            : 'border-white/60 text-white'
                        : isFavourited
                          ? 'border-[#181818] bg-[#181818] text-white'
                          : 'border-[#181818] text-[#181818]',
                    className,
                )}
            >
                <svg
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    className="max-md:size-5 md:size-[1.5rem]"
                    fill={isFavourited ? 'currentColor' : 'none'}
                    stroke="currentColor"
                    strokeWidth="1.6"
                >
                    <path d="M6 4h12v16l-6-4-6 4V4z" />
                </svg>
            </button>
            {followSelector}
        </>
    );
};
